<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         ref="workTemplateModal"
         class="p-fluid"
         id="work-template-modal"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
        {{ itemData?.id ? $t('workTemplateEditModalTitle') : $t('workTemplateCreateModalTitle') }}
      </div>
    </template>

    <Spinner v-if="dataIsSending"/>

    <div v-else class="p-formgrid p-grid p-pt-3 p-mb-0 p-pb-0">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="work-template-name"
                     v-model.trim="itemData.name"
                     :class="{'p-invalid': submitted && !itemData.name}"
                     maxlength="100"
                     autocomplete="off"/>
          <label for="work-template-name">
            {{ $t('Name') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.name">
          {{ $t('Required field') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <LimitedCharsTextarea :submitted="submitted"
                              v-model="itemData.description"
                              :maxChars="descriptionMaxChars"
                              :rows="2"
                              :id="'work-template-modal-description'"
                              :label="$t('Description')"/>
      </div>

      <ul class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 add-position-buttons background-shema-color p-mb-3">
        <li>
          <Button @click="addNewPosition(1)"
                  v-tooltip.top="$t('lineItemAddLaborTooltip')"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-hummer"/>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(2)"
                  v-tooltip.top="$t('lineItemAddSpecialOrderTooltip')"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-truck"/>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(3)"
                  v-tooltip.top="$t('lineItemAddStockItemTooltip')"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-package"/>
          </Button>
        </li>
        <li>
          <Button @click="addNewPosition(4)"
                  v-tooltip.top="$t('lineItemAddFreePositionTooltip')"
                  class="p-button-outlined add-position-button">
            <i class="ti-plus p-mr-1"/>
            <i class="ti-pencil"/>
          </Button>
        </li>
      </ul>

      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 work-templates-table-wrapper p-mb-2"
           @dragover.prevent="handleSortableBodyDragOver"
           @drop.prevent="handleSortableBodyDrop">
        <table class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows positions-table">
          <thead class="p-datatable-thead">
          <tr>
            <th class="table-title__icon"></th>
            <th class="table-title__name--first"></th>
            <th class="table-title__name--second"></th>
            <th class="table-title__name--last">{{ $t('Name') }}</th>
            <th class="table-title__qty">{{ $t('Qty') }}</th>
            <th class="table-title__purchase">{{ $t('purchasePriceShortLabel') }}</th>
            <th class="table-title__sell">{{ $t('sellingPriceShortLabel') }}</th>
            <th class="table-title__margin">{{ $t('markupShortLabel') }}</th>
            <th class="table-title__discount">
              {{ $t('discountShortLabel') }}<span style="font-weight: 300">(%)</span>
            </th>
            <th class="table-title__sum">{{ $t('Sum') }}</th>
            <th class="table-title__action-btn"></th>
          </tr>
          </thead>

          <tbody class="p-datatable-tbody table">
          <tr v-if="!positions.length">
            <td colspan="11" class="p-text-center p-p-4">
              {{ $t('No data available') }}
            </td>
          </tr>

          <tr v-for="(position, index) of positions"
              :key="position.uid || index"
              class="sortable-row"
              :class="getSortableRowDragClass(index)"
              @dragenter.prevent="handleSortableDragEnter($event, index)"
              @dragover.prevent="handleSortableDragOver($event, index)"
              @dragleave="handleSortableDragLeave($event, index)"
              @drop.prevent="handleSortableDrop($event, index)">
            <td class="position-type-sort-cell">
              <div class="position-sort-wrap">
                <button type="button"
                        class="position-sort-handle"
                        :class="{'is-disabled': !canSortRows()}"
                        :draggable="canSortRows()"
                        :disabled="!canSortRows()"
                        @dragstart="handleSortableDragStart($event, index)"
                        @dragend="handleSortableDragEnd">
                  <i :class="positionIcon(position.type)"/>
                </button>

                <div v-if="canSortRows() && showSortableStepButtons()"
                     class="position-sort-actions">
                  <button type="button"
                          :disabled="!canMoveSortableRowStep(index, -1)"
                          @click="moveSortableRowStep(index, -1)">
                    <i class="ti-angle-up"/>
                  </button>
                  <button type="button"
                          :disabled="!canMoveSortableRowStep(index, 1)"
                          @click="moveSortableRowStep(index, 1)">
                    <i class="ti-angle-down"/>
                  </button>
                </div>
              </div>
            </td>

            <template v-if="position.type === 1">
              <td>
                <CustomDropdown class="table-body__name-input--first"
                                :class="{'p-invalid warning-background': submitted && !position.selectedService}"
                                data-key="id"
                                v-model="position.selectedService"
                                @change="changeService(position)"
                                @filter="searchServices($event, position)"
                                :filterPlaceholder="$t('lineItemSearchServicePlaceholder')"
                                :placeholder="$t('lineItemSelectServicePlaceholder')"
                                :options="position.servicesData || servicesData"
                                :filterFields="['code', 'name']"
                                :filter="true"
                                optionLabel="name"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ slotProps.value.name }}</span>
                    </div>
                    <span v-else>{{ $t('lineItemSelectServicePlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div v-if="slotProps.option">
                      <div>{{ slotProps.option.name }}</div>
                      <span v-if="slotProps.option.code" style="font-size: 0.9rem">
                          {{ $t('Code') }}: {{ slotProps.option.code }}
                        </span>
                    </div>
                  </template>
                </CustomDropdown>
              </td>
              <td>
                <InputText disabled
                           v-model.trim="position.code"
                           :placeholder="$t('Code')"
                           class="table-body__name-input--code position-secondary-field"
                           autocomplete="off"/>
              </td>
            </template>

            <template v-else-if="position.type === 2">
              <td>
                <div class="dropdown-wrapper table-body__name-input--first">
                  <CustomDropdown class="dropdown__select dropdown__select--border-radius"
                                  :searchData="position.searchData"
                                  v-model="position.selectedSupplier"
                                  data-key="id"
                                  :options="position.suppliersData || suppliersData"
                                  optionLabel="company_name"
                                  :filterFields="['name', 'company_name', 'reg_number']"
                                  :filter="true"
                                  :createButton="false"
                                  @change="changeSupplier(position)"
                                  @filter="searchSuppliers($event, position)"
                                  :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                                  :placeholder="$t('lineItemSelectSupplierPlaceholder')"
                                  :showClear="true">
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div>{{ slotProps.value.company_name }} <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></div>
                      </div>
                      <span v-else>{{ $t('lineItemSelectSupplierPlaceholder') }}</span>
                    </template>
                    <template #option="slotProps">
                      <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
                    </template>
                  </CustomDropdown>
                </div>
              </td>
              <td>
                <InputText :placeholder="$t('Code')"
                           v-model.trim="position.code"
                           @input="updateCode(position)"
                           class="table-body__name-input--code position-secondary-field"
                           autocomplete="off"/>
              </td>
            </template>

            <template v-else-if="position.type === 3">
              <td>
                <div class="dropdown-wrapper table-body__name-input--first">
                  <CustomDropdown class="dropdown__select dropdown__select--border-radius"
                                  :class="{'p-invalid warning-background': submitted && !position.selectedWarehouseItem}"
                                  :searchData="position.searchData"
                                  data-key="id"
                                  v-model="position.selectedWarehouseItem"
                                  @change="changeWarehouseItem(position)"
                                  @filter="searchWarehouseItems($event, position)"
                                  :filterPlaceholder="$t('lineItemSearchStockItemPlaceholder')"
                                  :placeholder="$t('lineItemSelectStockItemPlaceholder')"
                                  :options="position.warehouseItemsData || warehouseItemsData"
                                  :filter="true"
                                  :filterFields="['code', 'raw_code', 'name']"
                                  optionLabel="name"
                                  :showClear="false">
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <span>{{ slotProps.value.name }}</span>
                      </div>
                      <span v-else>{{ $t('lineItemSelectStockItemPlaceholder') }}</span>
                    </template>
                    <template #option="slotProps">
                      <div>
                        <div>{{ slotProps.option.name }}<span v-if="slotProps.option.code">, {{ $t('Code') }}: {{ slotProps.option.code }}</span></div>
                        <span style="font-size: 0.9rem">
                            {{ $t('Qty') }}: {{ formatDecimal(slotProps.option.qty) }},
                            {{ $t('reservedShortLabel') }}: {{ formatDecimal(slotProps.option.reserved_qty) }},
                            {{ $t('lineItemAvailableQtyLabel') }}: {{ formatDecimal(slotProps.option.qty - slotProps.option.reserved_qty) }}
                          </span>
                      </div>
                    </template>
                  </CustomDropdown>
                </div>
              </td>
              <td>
                <InputText disabled
                           :placeholder="$t('Code')"
                           v-model.trim="position.code"
                           class="table-body__name-input--code position-secondary-field"
                           autocomplete="off"/>
              </td>
            </template>

            <template v-else>
              <td>
                <InputText disabled class="table-body__name-input--first"/>
              </td>
              <td>
                <InputText :placeholder="$t('Code')"
                           v-model.trim="position.code"
                           @input="updateCode(position)"
                           class="table-body__name-input--code"
                           autocomplete="off"/>
              </td>
            </template>

            <td>
              <InputText :placeholder="$t('Name')"
                         v-model.trim="position.name"
                         :disabled="(position.type === 1 && !position.selectedService) || (position.type === 3 && !position.selectedWarehouseItem)"
                         class="table-body__name-input--name"
                         :class="{'p-invalid warning-background': submitted && !position.name}"
                         maxlength="100"
                         autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.qty"
                           @input="updatePosition(position, $event.value, 'qty', 99999, 0)"
                           :inputClass="{'p-invalid warning-background': submitted && !position.qty}"
                           class="table-body__qty-input"
                           mode="decimal"
                           :min="0"
                           :max="99999"
                           :showButtons="true"
                           :minFractionDigits="position.type === 3 && !Number(position.is_decimal) ? 0 : 2"
                           :maxFractionDigits="position.type === 3 && !Number(position.is_decimal) ? 0 : 2"
                           autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.purchase_price"
                           @input="updatePosition(position, $event.value, 'purchase_price', 1000000, 0)"
                           @focus="startPriceEditing(position)"
                           @blur="stopPriceEditing"
                           class="table-body__purchase-input"
                           :inputClass="{
                             'p-invalid warning-background': isPriceWarningVisible(position)
                           }"
                           mode="decimal"
                           :min="0"
                           :max="1000000"
                           :minFractionDigits="2"
                           :maxFractionDigits="4"
                           autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.sell_price"
                           @input="updatePosition(position, $event.value, 'sell_price', 1000000, 0)"
                           @focus="startPriceEditing(position)"
                           @blur="stopPriceEditing"
                           class="table-body__sell-input"
                           :inputClass="{
                            'p-invalid warning-background': submitted && !position.sell_price ||
                                isPriceWarningVisible(position)
                          }"
                           mode="decimal"
                           :min="0"
                           :max="1000000"
                           :minFractionDigits="2"
                           :maxFractionDigits="4"
                           autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.margin"
                           @input="markupOnInput(position, $event.value)"
                           :disabled="position.type === 1 || position.type === 3 || (position.discount && +position.discount !== 0)"
                           class="table-body__margin-input"
                           mode="decimal"
                           :min="0"
                           :max="1000000"
                           :minFractionDigits="0"
                           :maxFractionDigits="2"
                           autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.discount"
                           @input="updatePosition(position, $event.value, 'discount', 100, 0)"
                           @focus="startPriceEditing(position)"
                           @blur="stopPriceEditing"
                           class="table-body__discount-input"
                           mode="decimal"
                           :min="0"
                           :max="100"
                           :minFractionDigits="0"
                           :maxFractionDigits="2"
                           autocomplete="off"/>
            </td>


            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="position.sum"
                           class="table-body__sum-input"
                           mode="decimal"
                           disabled
                           :minFractionDigits="2"
                           :maxFractionDigits="4"/>
            </td>

            <td>
              <div class="p-d-flex p-jc-end">
                <RemoveButton @click="removePosition(index)"/>
              </div>
            </td>
          </tr>
          </tbody>
          <!--          <tfoot>-->
          <!--            <tr>-->
          <!--              <td colspan="11"></td>-->
          <!--            </tr>-->
          <!--          </tfoot>-->
        </table>
      </div>
      <div v-if="positions.length" class="p-col-12 p-d-flex p-jc-end p-mt-2 p-mb-2">
        <div class="template-summary">
          <span class="p-mr-2">{{ $t('Sum without tax') }}:</span>
          <strong>{{ formatCurrency(templateSubtotal) }}</strong>
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>

  <ConfirmModal :visible="confirmSellingPriceIsTooLowModal"
                :title="$t('Confirmation')"
                :text="$t('sellingPriceLowerThanPurchaseAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmSellingPriceIsTooLowModal"
                @confirm="saveItemAnyway"/>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import formatMixins from '@/mixins/formatMixins'
import generateMixins from '@/mixins/generateMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import modalWithPositionsMixins from '@/mixins/modalWithPositionsMixins'
import RemoveButton from '@/components/DataTable/RemoveButton'
import sortingMixins from "@/mixins/sortingMixins";
import { computeByContext } from '@/utils/pricing';
import LimitedCharsTextarea from "@/components/LimitedCharsTextarea.vue";

const TYPE_SERVICE = 1
const TYPE_REQUEST = 2
const TYPE_WAREHOUSE = 3
// const TYPE_FREE = 4


export default {
  name: 'WorkTemplateModal',
  mixins: [
    formatMixins,
    generateMixins,
    showErrorsMixins,
    modalWithPositionsMixins,
    sortingMixins
  ],
  components: {
    RemoveButton,
    LimitedCharsTextarea
  },
  emits: ['close', 'update-items'],
  data() {
    return {
      confirmSellingPriceIsTooLowModal: false,
      ignoreSellingPriceIsTooLow: false,
      descriptionMaxChars: 512,
      itemData: {},
      positions: [],
      submitted: false,
      dataIsSending: false,
      disableSaveButton: false,
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.constructItemData()
      } else {
        this.resetLocalData()
      }
    },
    item() {
      if (this.visible) {
        this.constructItemData()
      }
    },
  },
  methods: {
    constructItemData() {
      this.clearSortableDragState()
      this.submitted = false
      this.disableSaveButton = false
      this.dataIsSending = false

      if (!this.item?.id) {
        this.itemData = {
          name: null,
          description: null,
        }
        this.positions = []
        return
      }

      this.itemData = {
        id: this.item.id,
        name: this.item.name || null,
        description: this.item.description || null,
      }

      this.positions = Array.isArray(this.item.positions)
          ? this.item.positions.map(this.constructPosition)
          : []
    },

    resetLocalData() {
      this.clearSortableDragState()
      this.itemData = {}
      this.positions = []
      this.submitted = false
      this.dataIsSending = false
      this.disableSaveButton = false

      this.confirmSellingPriceIsTooLowModal = false
      this.ignoreSellingPriceIsTooLow = false
      this.editingPricePositionUid = null
    },

    constructPosition(row = {}) {
      const type = Number(row.type)

      const position = {
        uid: row.uid || this.makeUid(),
        id: row.id || null,
        type,
        service_id: row.service_id || null,
        supplier_id: row.supplier_id || null,
        warehouse_item_id: row.warehouse_item_id || null,
        selectedService: row.service || row.selectedService || null,
        selectedSupplier: row.supplier || row.selectedSupplier || null,
        selectedWarehouseItem: row.warehouseItem || row.selectedWarehouseItem || null,
        servicesData: row.service ? [row.service] : null,
        suppliersData: row.supplier ? [row.supplier] : null,
        warehouseItemsData: row.warehouseItem ? [row.warehouseItem] : null,
        code: row.code || null,
        raw_code: row.raw_code || null,
        name: row.name || null,
        qty: this.toNumber(row.qty ?? 1, 1),
        purchase_price: this.toNumber(row.purchase_price ?? 0),
        purchase_sum: 0,
        sell_price: this.toNumber(row.sell_price ?? 0),
        discount: this.toNumber(row.discount ?? 0),
        discount_sum: 0,
        sum: 0,
        margin: null,
        comment: row.comment || null,
        status: Number(row.status ?? 1),
        sorting: row.sorting || this.findPositionsLastSortingValue(),
        is_decimal: row.warehouseItem?.is_decimal || row.selectedWarehouseItem?.is_decimal || 0,
      }

      this.calculatePositionSummary(position)
      this.updateMarkup(position)

      return position
    },

    addNewPosition(type) {
      this.submitted = false

      const position = this.constructPosition({
        type,
        qty: 1,
        purchase_price: 0,
        sell_price: 0,
        discount: 0,
        status: 1,
        sorting: this.findPositionsLastSortingValue(),
      })

      position.servicesData = this.servicesData
      position.suppliersData = this.suppliersData
      position.warehouseItemsData = this.warehouseItemsData

      this.positions.push(position)
    },

    removePosition(index) {
      this.positions.splice(index, 1)
      this.setNewSorting()
    },

    setNewSorting() {
      this.positions.forEach((position, index) => {
        position.sorting = index + 1
      })
    },

    changeService(position) {
      const service = position.selectedService

      position.service_id = service?.id || null

      if (!service) {
        position.code = null
        position.raw_code = null
        position.name = null
        position.purchase_price = 0
        position.sell_price = 0

        this.calculatePositionSummary(position)
        this.updateMarkup(position)

        return
      }

      position.code = service.code || null
      position.raw_code = service.code ? this.generateRawCode(service.code) : null
      position.name = service.name || null
      position.purchase_price = this.toNumber(service.purchase ?? 0)
      position.sell_price = this.toNumber(service.sell ?? 0)

      this.calculatePositionSummary(position)
      this.updateMarkup(position)
    },

    changeSupplier(position) {
      position.supplier_id = position.selectedSupplier?.id || null
    },

    changeWarehouseItem(position) {
      const item = position.selectedWarehouseItem

      position.warehouse_item_id = item?.id || null

      if (!item) {
        position.code = null
        position.raw_code = null
        position.name = null
        position.purchase_price = 0
        position.sell_price = 0
        position.is_decimal = 0

        this.calculatePositionSummary(position)
        this.updateMarkup(position)

        return
      }

      position.code = item.code || null
      position.raw_code = item.raw_code || (item.code ? this.generateRawCode(item.code) : null)
      position.name = item.name || null
      position.purchase_price = this.toNumber(item.purchase_price ?? 0)
      position.sell_price = this.toNumber(item.sell_price ?? 0)
      position.is_decimal = item.is_decimal || 0

      this.calculatePositionSummary(position)
      this.updateMarkup(position)
    },

    updateCode(position) {
      position.raw_code = position.code ? this.generateRawCode(position.code) : null
    },

    updatePosition(position, value, param, maxValue = null, minValue = null) {
      if (!position || !param) return

      let nextValue = value

      if ((maxValue || maxValue === 0) && nextValue > maxValue) {
        nextValue = maxValue
      }

      if ((minValue || minValue === 0) && nextValue < minValue) {
        nextValue = minValue
      }

      position[param] = nextValue

      if (param === 'purchase_price' && (Number(position.type) === 2 || Number(position.type) === 4)) {
        const locationData = this.$store.getters.locationData

        position.sell_price = computeByContext(
            nextValue,
            locationData,
            'orders'
        )
      }

      this.calculatePositionSummary(position)

      if (param !== 'markup') {
        this.updateMarkup(position)
      }
    },

    calculatePositionSummary(position) {
      if (!position) return

      const qty = this.toNumber(position.qty)
      const purchasePrice = this.toNumber(position.purchase_price)
      const sellPrice = this.toNumber(position.sell_price)
      const discount = this.toNumber(position.discount)

      const purchaseSum = purchasePrice * qty
      const rawNet = (sellPrice - ((discount / 100) * sellPrice)) * qty
      const net = Number(this.formatDecimal(rawNet, false, 2))
      const discountSum = Number(this.formatDecimal((sellPrice * qty) - rawNet, false, 2))

      position.purchase_sum = purchaseSum
      position.sum = net
      position.discount_sum = discountSum
    },

    validate() {
      if (!this.itemData.name) {
        return false
      }

      if (!this.positions.length) {
        this.$toast.add({
          severity: 'error',
          detail: this.$t('workTemplatePositionsRequiredMsg'),
          life: settings.toastLife,
        })
        return false
      }

      return !this.positions.some(position => {
        if (!position.name || !Number(position.qty)) return true
        if (position.type === TYPE_SERVICE && !position.selectedService) return true
        if (position.type === TYPE_WAREHOUSE && !position.selectedWarehouseItem) return true

        return false
      })
    },

    async saveItem() {
      this.submitted = true

      if (!this.validate()) {
        return false
      }

      if (!this.ignoreSellingPriceIsTooLow && this.hasPositionWithSellingPriceLowerThanPurchase()) {
        this.openConfirmSellingPriceIsTooLowModal()
        return
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      const payload = {
        name: this.itemData.name?.trim() || null,
        description: this.itemData.description?.trim() || null,
        positions: this.generatePositionsData(),
      }

      if (this.itemData.id) {
        payload.id = this.itemData.id
      }

      const url = this.itemData.id ? 'work-template/update' : 'work-template/create'

      try {
        const {status, data} = await httpClient.post(url, payload)
        const successStatus = this.itemData.id ? 200 : 201

        if (status === successStatus && data?.success) {
          const id = data.id || this.itemData.id

          this.$emit('update-items', id)
          this.$toast.add({
            severity: 'success',
            detail: this.$t(this.itemData.id ? 'Data updated' : 'Data saved'),
            life: settings.toastLife,
          })
          this.close()
        } else if (data?.error) {
          this.$toast.add({
            severity: 'error',
            summary: data.error.summary,
            detail: data.error.detail,
            life: settings.toastLife,
          })
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.disableSaveButton = false
        this.dataIsSending = false
      }
    },

    generatePositionsData() {
      return this.positions.map((position, index) => ({
        id: position.id || null,
        type: Number(position.type),
        service_id: position.selectedService?.id || position.service_id || null,
        supplier_id: position.selectedSupplier?.id || position.supplier_id || null,
        warehouse_item_id: position.selectedWarehouseItem?.id || position.warehouse_item_id || null,
        code: position.code ? position.code.trim() : null,
        raw_code: position.raw_code || null,
        name: position.name ? position.name.trim() : null,
        qty: position.qty,
        purchase_price: position.purchase_price || 0,
        sell_price: position.sell_price || 0,
        discount: position.discount || 0,
        comment: position.comment || null,
        status: 1,
        sorting: index + 1,
      }))
    },

    positionIcon(type) {
      if (Number(type) === TYPE_SERVICE) return 'ti-hummer'
      if (Number(type) === TYPE_REQUEST) return 'ti-truck'
      if (Number(type) === TYPE_WAREHOUSE) return 'ti-package'

      return 'ti-pencil'
    },

    makeUid() {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`
    },

    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    templateSubtotal() {
      return (this.positions || [])
          .filter(position => Number(position.status ?? 1) === 1)
          .reduce((sum, position) => {
            return sum + this.toNumber(position.sum)
          }, 0)
    },
    // computedWindowSize() {
    //   if (this.$store.state.mobileLayout) {
    //     return {position: 'absolute', top: 0, width: '95%', marginTop: '10px'}
    //   } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
    //     return {position: 'absolute', top: 0, width: '90%', marginTop: '25px'}
    //   }
    //
    //   return {position: 'absolute', top: 0, width: '90%', maxWidth: '1100px', marginTop: '50px'}
    // },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '95%'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '90%'}
      } else {
        return {position: 'absolute', top: 0, width: '90%', maxWidth: '1300px'}
      }
    },
  },
}
</script>

<style scoped lang="scss">
.work-templates-table-wrapper {
  //overflow-x: auto;
  //overflow-y: visible;
  overflow-y: auto;

  .positions-table {
    width: 100%;
    border-collapse: collapse;
    .table-body__name-input--first {
      min-width: 150px;
      max-width: 180px;
      //border: 1px solid pink;
    }
    .table-body__name-input--code {
      min-width: 150px;
      max-width: 180px;
    }
    .table-body__name-input--name {
      min-width: 200px;
      max-width: 240px;
      //border: 1px solid pink;
    }
    .table-body__qty-input {
      min-width: 85px;
      max-width: 100px;
      //width: 90px;
    }
    .table-body__purchase-input {
      min-width: 50px;
      max-width: 110px;
      //width: 80px;
    }
    .table-body__sell-input {
      min-width: 50px;
      max-width: 110px;
      //width: 80px;
    }

    .table-body__margin-input {
      min-width: 50px;
      max-width: 110px;
      //width: 80px;
    }

    .table-body__discount-input {
      min-width: 50px;
      max-width: 100px;
      //width: 80px;
    }

    .table-body__sum-input {
      min-width: 70px;
      max-width: 130px;
      //width: 80px;
    }
  }
}


</style>